import { useContext, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import toastr from "toastr";
import API from "../../../utils/API";
import AlertModal from "../../../utils/components/AlertModal";
import Input from "../../../utils/components/Input";
import Modal from "../../../utils/components/Modal";
import SelectBox from "../../../utils/components/SelectBox";
import Textarea from "../../../utils/components/Textarea";
import Button from "../../../utils/components/Button";
import { BsImageAlt } from "react-icons/bs";
import { useLanguage } from "../../../utils/hooks/useLanguage";
import { RequestContext } from "../../../utils/context/RequestContext";

const AddNewCompany = ({
  isClickable,
  setIsClickable,
  show,
  setShow,
  translate,
  refreshCompanies,
}) => {
  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    short_name: "",
    address: "",
    fax: "",
    email: "",
    projects: [],
    telephone: "",
    web_address: "",
  });

  const [projects, setProjects] = useState([]);
  // Optional; kept out of companyInfo so the required-fields check ignores it
  const [logo, setLogo] = useState("");

  const language = useLanguage();

  const { setRequestLoading } = useContext(RequestContext);

  useEffect(() => {
    API.getProjects({ type: "dropdown" }).then(({ data: res }) => {
      setProjects(res.data);
    });
  }, []);

  useEffect(() => {
    if (!show) {
      setCompanyInfo({
        name: "",
        short_name: "",
        address: "",
        email: "",
        fax: "",
        projects: [],
        telephone: "",
        web_address: "",
      });
      setLogo("");
    }
  }, [show]);

  const handleChange = (e) => {
    setCompanyInfo({
      ...companyInfo,
      [e.target.name]: e.target.value,
    });
  };

  const requiredTitle = (text) => (
    <>
      {text} <span className="text-danger">*</span>
    </>
  );

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result.includes("data:image/")) setLogo(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAddCompany = (e) => {
    e.preventDefault();

    const check = Object.values(companyInfo).every(
      (value) => value !== "" && value.length > 0
    );

    if (!check) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return false;
    }

    setIsClickable(false);

    setRequestLoading(true);

    const data = {
      ...companyInfo,
      projects: companyInfo.projects.map((project) => project.value),
      logo: logo ? logo.split(",")[1] : "",
    };

    API.postData(`companies`, data).then(({ data: res }) => {
      toastr.clear();
      toastr.success(res.message);
      refreshCompanies(res.data);
      setShow(false);
    });
  };

  return (
    <div className="new-company-modal">
    <Modal
      title={translate("companies.add_company_modal.title")}
      positiveButtonNode={translate("save")}
      negativeButtonNode={translate("cancel")}
      positiveButtonHandler={handleAddCompany}
      modalSize="lg"
      show={show}
      setShow={(show) => {
        setShow(show);
      }}
      disableButtons={!isClickable}
    >
      <div className="d-flex flex-wrap align-items-center">
        <div className="col-12 col-md-4 mt-2 pe-3">
          <div className="input-group">
            <label
              className="form-label text-uppercase fw-bold pb-0"
              htmlFor="new-company-logo"
            >
              {translate("companies.add_company_modal.logo")}
            </label>
            <fieldset className="company-logo-area w-100">
              <label
                htmlFor="new-company-logo"
                className="w-100 d-flex justify-content-center align-items-center"
              >
                {logo ? (
                  <img src={logo} alt="Company Logo" className="w-100" />
                ) : (
                  <span className="icon rounded-circle d-flex justify-content-center align-items-center">
                    <BsImageAlt />
                  </span>
                )}
              </label>
              <input
                type="file"
                accept="image/*"
                id="new-company-logo"
                className="d-none"
                onChange={handleLogoChange}
              />
            </fieldset>
            {logo && (
              <Button className="btn btn-danger mt-2" onClick={() => setLogo("")}>
                {translate("companies.add_company_modal.remove_logo")}
              </Button>
            )}
          </div>
        </div>
        <div className="col-12" />
        <div className="col-md-6 mt-4 pe-3">
          <Input
            type="text"
            name="name"
            value={companyInfo.name}
            title={requiredTitle(translate("companies.modal-input-titles.company-name").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.company_name_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mt-4 pe-3">
          <Input
            type="text"
            name="short_name"
            value={companyInfo.short_name}
            title={requiredTitle(translate("companies.modal-input-titles.company-short-name").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.company_short_name_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mt-4 pe-3">
          <Input
            type="tel"
            name="telephone"
            value={companyInfo.telephone}
            title={requiredTitle(translate("companies.modal-input-titles.company-phone").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.phone_number_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mt-4 pe-3">
          <Input
            type="fax"
            name="fax"
            value={companyInfo.fax}
            title={requiredTitle(translate("companies.modal-input-titles.company-fax-number").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.fax_number_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mt-4 pe-3">
          <Input
            type="email"
            name="email"
            value={companyInfo.email}
            title={requiredTitle(translate("companies.modal-input-titles.company-email").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.contact_email_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mt-4 pe-3">
          <Input
            type="text"
            name="web_address"
            value={companyInfo.web_address}
            title={requiredTitle(translate("companies.modal-input-titles.company-web-site").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.web_address_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-12 mt-4 pe-3">
          <Textarea
            name="address"
            value={companyInfo.address}
            title={requiredTitle(translate("companies.modal-input-titles.company-address").toLocaleUpperCase(language))}
            placeholder={translate(
              "companies.add_company_modal.company_address_input_placeholder"
            )}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 mt-4 pe-3">
          <p className="headTitle text-secondary fw-bold mb-1 ms-1">
            {requiredTitle(translate("companies.modal-input-titles.projects"))}
          </p>
          {show && (
            <SelectBox
              options={projects.map((project) => ({
                value: project.id,
                label: project.name,
              }))}
              value={companyInfo.projects}
              isMulti={true}
              placeholder={translate(
                "companies.add_company_modal.projects_input_placeholder"
              )}
              onChange={(tags) =>
                setCompanyInfo({ ...companyInfo, projects: tags })
              }
            />
          )}
        </div>
      </div>
    </Modal>
    </div>
  );
};

export default AddNewCompany;
